import type { PsvSourceFamily } from "@api/shared-runtime/psv-monitoring";
import {
  MONITORING_COPY,
  PREVIEW_PROVIDER_LABEL_LIMIT,
  SOURCE_LABELS,
} from "./monitoring-constants";
import {
  describeCoverageGap,
  plural,
  providerIdLabel,
} from "./monitoring-format";
import type { ProgramEditPreview } from "./monitoring-types";

type Props = {
  preview: ProgramEditPreview | undefined;
  isPending: boolean;
};

/** Live impact of a rule edit. The container is a polite live region that is
 * only filled once the debounced preview settles, so assistive tech hears one
 * announcement per edit instead of one per keystroke. */
export function ImpactSummary({ preview, isPending }: Props) {
  return (
    <section
      aria-label="Impact of this change"
      aria-live="polite"
      aria-busy={isPending}
      className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm"
    >
      <h3 className="font-medium">Impact</h3>
      {!preview && (
        <p className="text-muted-foreground">
          {isPending ? "Calculating impact…" : "Impact is unavailable."}
        </p>
      )}
      {preview && !isPending && <ImpactDetails preview={preview} />}
      {preview && isPending && (
        <p className="text-muted-foreground">Updating impact…</p>
      )}
    </section>
  );
}

function ImpactDetails({ preview }: { preview: ProgramEditPreview }) {
  const { starting, stopping, sourceChanges, scope } = preview;
  return (
    <div className="space-y-2">
      <p>
        Covers{" "}
        {plural({ count: preview.coveredProviderCount, noun: "provider" })}.
      </p>
      {starting.count > 0 && (
        <ProviderChange
          verb="will start"
          count={starting.count}
          providerUserIds={starting.providerUserIds}
        />
      )}
      {stopping.count > 0 && (
        <ProviderChange
          verb="will stop"
          count={stopping.count}
          providerUserIds={stopping.providerUserIds}
        />
      )}
      <SourceChangeLine label="Sources added" families={sourceChanges.added} />
      <SourceChangeLine
        label="Sources removed"
        families={sourceChanges.removed}
      />
      <SourceChangeLine
        label="State selection changed"
        families={sourceChanges.changed}
      />
      {scope.coverageGaps.map((gap) => (
        <p key={gap.family} className="text-amber-800">
          {describeCoverageGap(gap)}
        </p>
      ))}
      {starting.count > 0 && <p>{MONITORING_COPY.firstChecks}</p>}
      <p className="text-muted-foreground">{MONITORING_COPY.billing}</p>
      <p className="text-muted-foreground">{MONITORING_COPY.overlap}</p>
    </div>
  );
}

function ProviderChange({
  verb,
  count,
  providerUserIds,
}: {
  verb: string;
  count: number;
  providerUserIds: string[];
}) {
  const shown = providerUserIds.slice(0, PREVIEW_PROVIDER_LABEL_LIMIT);
  const hidden = count - shown.length;
  const labels = shown.map(providerIdLabel).join(", ");
  return (
    <p>
      {plural({ count, noun: "provider" })} {verb}
      {labels && `: ${labels}`}
      {hidden > 0 && ` +${hidden} more`}
    </p>
  );
}

function SourceChangeLine({
  label,
  families,
}: {
  label: string;
  families: PsvSourceFamily[];
}) {
  if (families.length === 0) {
    return null;
  }
  return (
    <p>
      {label}: {families.map((family) => SOURCE_LABELS[family]).join(", ")}
    </p>
  );
}
